import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { StarIcon, InformationCircleIcon, ShoppingBagIcon, BoltIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon, MagnifyingGlassIcon, ShareIcon } from '@heroicons/react/24/outline';
import SizeChartModal from '../../components/SizeChartModal';
import { useCart } from '../../context/CartContext';
import { HeartIcon as HeartSolidIcon, HeartIcon as HeartOutlineIcon } from '@heroicons/react/24/solid';
import { useWishlist } from '../../context/WishlistContext';
import { getApiUrl } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { clothingSizes, productHasClothingSizes } from '../../utils/productSize';
import ProductPrice from '../../components/ProductPrice';

const ProductDetail = () => {
  const { id } = useParams();
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('White');
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [showAddedToCart, setShowAddedToCart] = useState(false);
  const [addedToWishlist, setAddedToWishlist] = useState({});
  const { addToCart, cartItems, isInCart } = useCart();
  const navigate = useNavigate();
  const { isWishlisted, toggleWishlist, wishlist } = useWishlist();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(true);
  const [showShareSuccess, setShowShareSuccess] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    const fetchProduct = async () => {
      try {
        setLoading(true);
        // Add loading class to body to prevent scrolling
        document.body.classList.add('loading');
        
        const response = await fetch(getApiUrl(`/api/admin/products/${id}`), {
          headers: { 'x-admin': 'true' }
        });
        if (!response.ok) {
          throw new Error('Product not found');
        }
        const data = await response.json();
        setProduct(data);
        const availableSizes = clothingSizes(data?.sizes);
        setSelectedSize(availableSizes.includes('M') ? 'M' : availableSizes[0] || '');
        setLoading(false);
        // Remove loading class from body
        document.body.classList.remove('loading');
      } catch (err) {
        setError(err.message);
        setLoading(false);
        // Remove loading class from body on error too
        document.body.classList.remove('loading');
      }
    };

    if (id) {
      fetchProduct();
    }
    
    // Cleanup function to remove loading class when component unmounts
    return () => {
      document.body.classList.remove('loading');
    };
  }, [id]);

  // Helper to get _id from object or string
  const getId = val => (val && typeof val === 'object' && val._id ? val._id : val);

  // Force re-render when wishlist or cart changes
  useEffect(() => {
    // This effect will trigger re-render when wishlist or cart changes
  }, [wishlist, cartItems]);

  useEffect(() => {
    const fetchRelatedProducts = async () => {
      if (!product) return;
      try {
        setRelatedLoading(true);
        const response = await fetch(getApiUrl('/api/admin/products'), {
          headers: { 'x-admin': 'true' }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch related products');
        }
        const data = await response.json();
        let productsList = Array.isArray(data.products) ? data.products : Array.isArray(data) ? data : [];
        // Debug logs
        console.log('Current product:', product);
        console.log('All products:', productsList);
        // Filter related products based on normalized category and subcategory IDs
        const related = productsList
          .filter(p =>
            p._id !== product._id &&
            (
              getId(p.category) === getId(product.category) ||
              (product.subcategory && getId(p.subcategory) === getId(product.subcategory))
            )
          )
          .slice(0, 3);
        setRelatedProducts(related);
        setRelatedLoading(false);
      } catch (err) {
        console.error('Error fetching related products:', err);
        setRelatedLoading(false);
      }
    };
    if (product) {
      fetchRelatedProducts();
    }
  }, [product]);

  const handleZoom = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setPosition({ x, y });
    setZoomLevel(prev => Math.min(prev + 0.1, 3));
  };

  const handleUnzoom = () => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity, selectedSize, selectedColor);
      setShowAddedToCart(true);
      setTimeout(() => setShowAddedToCart(false), 2000);
    }
  };

  const handleShare = async () => {
    // Use production URL instead of localhost
    const productionUrl = 'https://thevastra.store';
    const productUrl = `${productionUrl}/product/${product._id}`;
    
    // Create engaging share text based on product type
    const getShareText = () => {
      const productName = product.name || 'Amazing Product';
      const productPrice = product.price ? `₹${product.price}` : '';
      
      // Professional Flipkart-style sharing format
      return `I ❤️ this stunning ${productName} from @theVastra.store!\nElegant. Festive. A total showstopper. ✨\n\n🛍️ Grab it here:`;
    };
    
    const shareText = getShareText();
    
    try {
      if (navigator.share) {
        // Use native sharing on mobile devices
        await navigator.share({
          title: product.name,
          text: shareText,
          url: productUrl,
        });
      } else {
        // Fallback to clipboard copy with full description
        await navigator.clipboard.writeText(shareText);
        setShowShareSuccess(true);
        setTimeout(() => setShowShareSuccess(false), 2000);
      }
    } catch (error) {
      console.error('Error sharing:', error);
      // Fallback to clipboard copy if native sharing fails
      try {
        await navigator.clipboard.writeText(shareText);
        setShowShareSuccess(true);
        setTimeout(() => setShowShareSuccess(false), 2000);
      } catch (clipboardError) {
        console.error('Error copying to clipboard:', clipboardError);
      }
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-white/80 flex items-center justify-center z-50 overflow-hidden">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The product you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/products')}
            className="px-6 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Zoom Modal */}
      {showZoomModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-6xl h-[80vh] bg-white rounded-2xl overflow-hidden">
            <button
              onClick={() => {
                setShowZoomModal(false);
                handleUnzoom();
              }}
              className="circular-button absolute top-4 right-4 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg flex items-center justify-center"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <div 
              className="w-full h-full flex items-center justify-center cursor-zoom-in"
              onWheel={(e) => {
                e.preventDefault();
                if (e.deltaY < 0) {
                  setZoomLevel(prev => Math.min(prev + 0.1, 3));
                } else {
                  setZoomLevel(prev => Math.max(prev - 0.1, 1));
                }
              }}
              onMouseMove={handleZoom}
              onMouseLeave={handleUnzoom}
            >
              <img
                src={product.images?.[imgIdx] || product.rentalImages?.[imgIdx] || 'https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=400&q=80'}
                alt={product.name}
                className="max-w-full max-h-full object-contain transition-all duration-200"
                style={{ 
                  transform: `scale(${zoomLevel})`,
                  transformOrigin: `${position.x * 100}% ${position.y * 100}%`
                }}
              />
            </div>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
              {product.images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setImgIdx(i);
                    handleUnzoom();
                  }}
                  className={`carousel-dot w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-2.5 md:h-2.5 lg:w-3 lg:h-3 rounded-full transition-all ${
                    i === imgIdx ? 'bg-pink-600' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`View image ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-10 mobile-top-spacing">
        {/* Remove the 'Rent Your Item' button */}
        {/* {user && (
          <div className="mb-8 flex justify-end">
            <button
              className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition"
              onClick={() => navigate('/rent/submit')}
            >
              Rent Your Item
            </button>
          </div>
        )} */}
        <div className="product-grid grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
          {/* Image Section with Carousel */}
          <div className="product-image-section relative flex flex-col items-center justify-center bg-gradient-to-br rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl">
            {/* {product.isNew && (
              <div className="absolute -top-3 left-6 z-10">
                <span className="bg-pink-600 text-white px-4 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                  New Arrival
                </span>
              </div>
            )} */}
            <div 
              className="card-image-container relative w-full aspect-[4/5] sm:aspect-[3/4] lg:aspect-[4/5] flex items-center justify-center cursor-zoom-in bg-gray-100"
              onClick={() => setShowZoomModal(true)}
            >
              <img
                src={product.images?.[imgIdx] || product.rentalImages?.[imgIdx] || 'https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=400&q=80'}
                alt={product.name}
                className="w-full h-full object-cover image-loading rounded-xl sm:rounded-2xl shadow-lg border-2 sm:border-4 border-white"
                onLoad={(e) => {
                  e.target.classList.remove('image-loading');
                  e.target.classList.add('image-loaded');
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div 
                className="absolute inset-0 image-fallback rounded-2xl"
                style={{ display: 'none' }}
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-pink-600 text-3xl font-bold">{product.name.charAt(0)}</span>
                  </div>
                  <p className="text-gray-600 text-lg font-medium">{product.name}</p>
                </div>
              </div>
              {product.images.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setImgIdx((imgIdx - 1 + product.images.length) % product.images.length);
                    }}
                    className="carousel-nav-button absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-700 rounded-full p-1.5 sm:p-2 shadow-lg flex items-center justify-center"
                    aria-label="Previous image"
                  >
                    <ChevronLeftIcon className="h-4 w-4 sm:h-6 sm:w-6" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setImgIdx((imgIdx + 1) % product.images.length);
                    }}
                    className="carousel-nav-button absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-700 rounded-full p-1.5 sm:p-2 shadow-lg flex items-center justify-center"
                    aria-label="Next image"
                  >
                    <ChevronRightIcon className="h-4 w-4 sm:h-6 sm:w-6" />
                  </button>
                  <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 flex space-x-1 sm:space-x-2">
                    {product.images.map((_, i) => (
                      <button
                        key={i}
                        onClick={(e) => {
                          e.stopPropagation();
                          setImgIdx(i);
                        }}
                        className={`carousel-dot w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-2.5 md:h-2.5 lg:w-3 lg:h-3 rounded-full transition-all ${
                          i === imgIdx ? 'bg-pink-600' : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                        aria-label={`View image ${i + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right Side Content */}
          <div className="product-details-section flex flex-col order-2 lg:order-2">
            {/* Details Card */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-6 lg:p-8">
                              <div className="space-y-3 sm:space-y-6">
                {/* Title and Price */}
                <div>
                  <div className="flex items-start justify-between gap-4">
                    <h1 className="text-lg sm:text-2xl lg:text-3xl font-extrabold text-gray-900 tracking-tight leading-tight">{product.name}</h1>
                    <button
                      onClick={handleShare}
                      className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white border-2 border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800 transition-all duration-200 shadow-sm hover:shadow-md flex-shrink-0"
                      title="Share this product"
                    >
                      <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                      </svg>
                    </button>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-3 sm:mt-2">
                    {(() => {
                      const reviews = Array.isArray(product.reviews) ? product.reviews : [];
                      const avgRating = reviews.length > 0
                        ? reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length
                        : 0;
                      return (
                        <div className="flex items-center">
                          {[1,2,3,4,5].map(i => (
                            <StarIcon
                              key={i}
                              className={`h-4 w-4 sm:h-5 sm:w-5 ${i <= Math.round(avgRating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                            />
                          ))}
                          <span className="ml-2 text-xs sm:text-sm text-gray-600 font-medium">
                            {avgRating > 0 ? avgRating.toFixed(1) : 'No'} ({reviews.length} reviews)
                          </span>
                        </div>
                      );
                    })()}
                    <div className="flex items-center gap-2">
                      <ProductPrice
                        product={product}
                        saleClassName="text-lg sm:text-2xl font-bold text-pink-600"
                        retailClassName="text-sm sm:text-lg text-gray-400 line-through"
                        showPercent
                      />
                    </div>
                  </div>
                </div>

                {/* Product Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Category:</span>
                    <span className="ml-2 text-gray-900 font-medium">{typeof product.category === 'object' ? product.category?.name : product.category}</span>
                  </div>
                  {product.subcategory && (
                    <div>
                      <span className="text-gray-500">Subcategory:</span>
                      <span className="ml-2 text-gray-900 font-medium">{typeof product.subcategory === 'object' ? product.subcategory?.name : product.subcategory}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Stock:</span>
                    <span className="ml-2 text-gray-900 font-medium">{product.stock} units</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <span className={`ml-2 font-medium ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {product.stock > 0 ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </div>
                </div>

                {/* Sizes */}
                {productHasClothingSizes(product) ? (
                <div>
                  <div className="flex items-center mb-3">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mr-2">Size</h3>
                    <button
                      type="button"
                      onClick={() => setShowSizeChart(true)}
                      className="text-pink-600 hover:text-pink-800 focus:outline-none"
                      aria-label="Show size chart"
                    >
                      <InformationCircleIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 sm:gap-3">
                    {clothingSizes(product.sizes).map((size) => (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full border text-sm sm:text-base font-semibold transition-all duration-150 ${
                            selectedSize === size
                              ? 'border-pink-600 bg-pink-100 text-pink-700 shadow'
                              : 'border-gray-300 text-gray-700 bg-white hover:bg-pink-50'
                          }`}
                        >
                          {size}
                        </button>
                    ))}
                  </div>
                  <SizeChartModal open={showSizeChart} onClose={() => setShowSizeChart(false)} />
                </div>
                ) : null}

                {/* Colors */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Color</h3>
                  <div className="flex flex-wrap gap-1.5 sm:gap-3">
                    {product.colors && product.colors.length > 0 ? (
                      product.colors.map((color) => (
                        <button
                          key={color}
                          onClick={() => setSelectedColor(color)}
                          className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full border text-sm sm:text-base font-semibold transition-all duration-150 ${
                            selectedColor === color
                              ? 'border-indigo-600 bg-indigo-100 text-indigo-700 shadow'
                              : 'border-gray-300 text-gray-700 bg-white hover:bg-indigo-50'
                          }`}
                        >
                          {color}
                        </button>
                      ))
                    ) : (
                      <p className="text-gray-500">No colors available</p>
                    )}
                  </div>
                </div>

                {/* Quantity */}
                <div className="flex items-center gap-4">
                  <h3 className="text-lg font-semibold text-gray-900">Quantity</h3>
                  <div className="flex items-center">
                    <button
                      onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-l-full bg-white text-base sm:text-lg font-bold hover:bg-gray-100 transition-colors"
                      disabled={quantity <= 1}
                    >
                      -
                    </button>
                    <span className="px-4 sm:px-6 py-1.5 sm:py-2 border-t border-b border-gray-300 bg-white text-base sm:text-lg font-semibold">{quantity}</span>
                    <button
                      onClick={() => setQuantity(prev => prev + 1)}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-r-full bg-white text-base sm:text-lg font-bold hover:bg-gray-100 transition-colors"
                      disabled={quantity >= product.stock}
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Description */}
                <div className="product-description border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Product Description</h3>
                  <p className="text-gray-700 text-base sm:text-lg leading-relaxed">{product.description}</p>
                  <div className="h-1 w-16 bg-gradient-to-r from-pink-400 to-indigo-400 rounded-full mt-4" />
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-3 sm:pt-4">
                  <button
                    onClick={handleAddToCart}
                    disabled={product.stock <= 0}
                    className={`flex items-center justify-center gap-2 w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl bg-gradient-to-r from-pink-500 to-indigo-500 text-white font-bold text-sm sm:text-base shadow-lg hover:from-pink-600 hover:to-indigo-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:ring-offset-2 ${
                      product.stock <= 0 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <ShoppingBagIcon className="h-6 w-6" />
                    {product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}
                  </button>
                  <button
                    className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl border-2 border-pink-500 text-pink-600 font-bold text-sm sm:text-base bg-white shadow hover:bg-pink-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:ring-offset-2"
                    onClick={() => navigate('/checkout')}
                    disabled={product.stock <= 0}
                  >
                    <BoltIcon className="h-6 w-6" />
                    Order Now
                  </button>
                </div>

                {/* Added to Cart Notification */}
                {showAddedToCart && (
                  <div className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg animate-fade-in-up">
                    Added to cart successfully!
                  </div>
                )}

                {/* Share Success Notification */}
                {showShareSuccess && (
                  <div className="fixed bottom-4 right-4 bg-blue-500 text-white px-6 py-3 rounded-lg shadow-lg animate-fade-in-up">
                    Product link copied to clipboard!
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product Tabs Section */}
      <div className="max-w-7xl mx-auto px-4 mt-16">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <Tabs product={product} />
        </div>
      </div>

      {/* You may also like */}
      <div className="max-w-7xl mx-auto px-4 mt-16 mb-20">
        <div className="flex items-center justify-center mb-6">
          <div className="flex items-center">
            <div className="h-px bg-gray-300 flex-1 w-8 sm:w-16"></div>
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 px-4">You may also like</h3>
            <div className="h-px bg-gray-300 flex-1 w-8 sm:w-16"></div>
          </div>
        </div>
        {relatedLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-600"></div>
          </div>
        ) : relatedProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {relatedProducts.map((relatedProduct) => (
              <div 
                key={`${relatedProduct._id}-${isWishlisted(relatedProduct._id)}`}
                className="group relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300 border border-gray-100"
              >
                <div className="card-image-container w-full h-64 sm:h-72 lg:h-80">
                  <img 
                    src={relatedProduct.images?.[0] || relatedProduct.rentalImages?.[0] || 'https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=400&q=80'} 
                    alt={relatedProduct.name} 
                    className="w-full h-full object-cover image-loading group-hover:scale-105" 
                    onLoad={(e) => {
                      e.target.classList.remove('image-loading');
                      e.target.classList.add('image-loaded');
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div 
                    className="absolute inset-0 image-fallback"
                    style={{ display: 'none' }}
                  >
                    <div className="text-center">
                      <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-pink-600 text-2xl font-bold">{relatedProduct.name.charAt(0)}</span>
                      </div>
                      <p className="text-gray-600 text-sm font-medium">{relatedProduct.name}</p>
                    </div>
                  </div>
                  <button
                    className="absolute top-3 right-3 bg-white/80 rounded-full p-2 shadow-md opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 z-10"
                    aria-label="Add to wishlist"
                    onClick={async () => {
                      await toggleWishlist(relatedProduct);
                      setAddedToWishlist(prev => ({
                        ...prev,
                        [relatedProduct._id]: true
                      }));
                      setTimeout(() => {
                        setAddedToWishlist(prev => ({
                          ...prev,
                          [relatedProduct._id]: false
                        }));
                      }, 1000);
                    }}
                  >
                    {isWishlisted(relatedProduct._id) ? (
                      <HeartSolidIcon className="w-6 h-6 text-pink-600" />
                    ) : (
                      <HeartOutlineIcon className="w-6 h-6 text-gray-500 hover:text-pink-600 transition-colors" />
                    )}
                  </button>
                  <button
                    className="absolute bottom-3 right-3 bg-white/90 rounded-full p-2 shadow-md opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 z-10"
                    aria-label="Add to cart"
                    onClick={async () => {
                      await addToCart(relatedProduct, 1, 'M', 'Default');
                      setShowAddedToCart(true);
                      setTimeout(() => setShowAddedToCart(false), 2000);
                    }}
                  >
                    <ShoppingBagIcon className={`w-6 h-6 ${isInCart(relatedProduct._id) ? 'text-indigo-600' : 'text-gray-500 hover:text-indigo-600 transition-colors'}`} />
                  </button>
                  {relatedProduct.stock <= 0 && (
                    <span className="absolute top-3 left-3 bg-red-600 text-white text-xs px-2 py-1 rounded-full font-semibold z-10 shadow">
                      Out of Stock
                    </span>
                  )}
                  {addedToWishlist[relatedProduct._id] && (
                    <span className="absolute top-3 left-3 bg-green-600 text-white text-xs px-2 py-1 rounded-full font-semibold z-10 shadow animate-pulse">
                      Added!
                    </span>
                  )}
                </div>
                <div className="p-3 sm:p-4">
                  <Link to={`/product/${relatedProduct._id}`} className="block">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">{relatedProduct.name}</h4>
                  </Link>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <StarIcon
                          key={i}
                          className={`h-4 w-4 ${
                            i < Math.round(relatedProduct.rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-600">
                      ({Array.isArray(relatedProduct.reviews) ? relatedProduct.reviews.length : (typeof relatedProduct.reviews === 'number' ? relatedProduct.reviews : 0)})
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <ProductPrice
                      product={relatedProduct}
                      saleClassName="text-lg sm:text-xl font-bold text-pink-600"
                      retailClassName="text-sm text-gray-400 line-through"
                      showPercent
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            No related products found
          </div>
        )}
      </div>
    </>
  );
};

export default ProductDetail;

// Tabbed section for Description, Material & Care, Shipping & Returns, Reviews
function Tabs({ product }) {
  const [tab, setTab] = useState('description');
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const { user } = useAuth();

  // Fetch reviews for this product
  useEffect(() => {
    if (tab === 'reviews' && product?._id) {
      setReviewsLoading(true);
      fetch(getApiUrl(`/api/products/${product._id}`))
        .then(res => res.json())
        .then(data => {
          setReviews(data.reviews || []);
          setReviewsLoading(false);
        })
        .catch(err => {
          console.error('Error fetching reviews:', err);
          setReviewsLoading(false);
        });
    }
  }, [tab, product?._id, submitSuccess]);

  // Check if user has already reviewed
  const hasReviewed = user && Array.isArray(reviews) && reviews.some(r => r.user && (r.user._id === user._id || r.user === user._id));

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');
    try {
      const res = await fetch(getApiUrl(`/api/products/${product._id}/reviews`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ rating, comment })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to submit review');
      }
      setSubmitSuccess('Review submitted!');
      setRating(0);
      setComment('');
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex gap-2 sm:gap-4 border-b border-gray-200 mb-4 overflow-x-auto">
        <button onClick={() => setTab('description')} className={`pb-2 px-1 sm:px-2 font-semibold transition text-sm sm:text-base whitespace-nowrap ${tab === 'description' ? 'border-b-2 border-pink-500 text-pink-600' : 'text-gray-500 hover:text-pink-500'}`}>Description</button>
        <button onClick={() => setTab('care')} className={`pb-2 px-1 sm:px-2 font-semibold transition text-sm sm:text-base whitespace-nowrap ${tab === 'care' ? 'border-b-2 border-pink-500 text-pink-600' : 'text-gray-500 hover:text-pink-500'}`}>Material & Care</button>
        <button onClick={() => setTab('shipping')} className={`pb-2 px-1 sm:px-2 font-semibold transition text-sm sm:text-base whitespace-nowrap ${tab === 'shipping' ? 'border-b-2 border-pink-500 text-pink-600' : 'text-gray-500 hover:text-pink-500'}`}>Shipping & Returns</button>
        <button onClick={() => setTab('reviews')} className={`pb-2 px-1 sm:px-2 font-semibold transition text-sm sm:text-base whitespace-nowrap ${tab === 'reviews' ? 'border-b-2 border-pink-500 text-pink-600' : 'text-gray-500 hover:text-pink-500'}`}>
          Reviews ({product?.reviews?.length || 0})
        </button>
      </div>
      <div className="min-h-[80px]">
        {tab === 'description' && <div className="text-gray-700">No additional description available.</div>}
        {tab === 'care' && <div className="text-gray-700">Material: 100% Cotton<br />Care: Machine wash cold, gentle cycle. Do not bleach. Tumble dry low. Iron on low if needed.</div>}
        {tab === 'shipping' && <div className="text-gray-700">Free shipping on orders above ₹1999. Easy replacement no returns. Express delivery available in select cities.</div>}
        {tab === 'reviews' && (
          <div className="space-y-4">
            {/* Review Form */}
            {user ? (
              hasReviewed ? (
                <div className="text-green-600 font-semibold mb-4">You have already reviewed this product.</div>
              ) : (
                <form onSubmit={handleReviewSubmit} className="mb-6 bg-gray-50 p-4 rounded-xl shadow flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Your Rating:</span>
                    {[1,2,3,4,5].map(star => (
                      <button type="button" key={star} onClick={() => setRating(star)} className="focus:outline-none">
                        <StarIcon className={`h-6 w-6 ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                      </button>
                    ))}
                  </div>
                  <textarea
                    className="w-full border rounded-lg p-2"
                    rows={3}
                    placeholder="Write your review..."
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    required
                  />
                  <button type="submit" className="bg-pink-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-pink-700 transition" disabled={submitting || !rating || !comment.trim()}>
                    {submitting ? 'Submitting...' : 'Submit Review'}
                  </button>
                  {submitError && <div className="text-red-600 text-sm mt-2">{submitError}</div>}
                  {submitSuccess && <div className="text-green-600 text-sm mt-2">{submitSuccess}</div>}
                </form>
              )
            ) : (
              <div className="text-pink-600 font-semibold mb-4">Please log in to add a review.</div>
            )}
            {reviewsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-pink-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading reviews...</p>
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No reviews yet. Be the first to review this product!</p>
              </div>
            ) : (
              <div className="space-y-6">
                {reviews.map((review, index) => (
                  <div key={index} className="border-b border-gray-100 pb-6 last:border-b-0">
                    {/* Star Rating */}
                    <div className="flex items-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <StarIcon
                          key={star}
                          className={`w-5 h-5 ${
                            star <= review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                          }`}
                        />
                      ))}
                      <span className="ml-2 text-sm text-gray-600">({review.rating}/5)</span>
                    </div>
                    
                    {/* Review Comment */}
                    <p className="text-gray-700 mb-3 italic">"{review.comment}"</p>
                    
                    {/* Review Images */}
                    {review.images && review.images.length > 0 && (
                      <div className="flex gap-2 mb-3 overflow-x-auto">
                        {review.images.map((image, imgIndex) => (
                          <img
                            key={imgIndex}
                            src={image}
                            alt={`Review ${index + 1}`}
                            className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                          />
                        ))}
                      </div>
                    )}
                    
                    {/* User Info */}
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-pink-400 to-indigo-400 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold text-xs">
                          {review.user?.name?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{review.user?.name || 'Anonymous'}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(review.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
} 